import type { ISurveyTranslationRow } from '@/features/survey/survey-translation-section'
import { Loader2 } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { SurveyTranslationSection } from '@/features/survey/survey-translation-section'
import { TRANSLATION_JOB_SCOPE } from '@/shared/constants/status'
import { showToast } from '@/shared/libs/toast'
import { Button } from '@/shared/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card'
import { ImageUploadField } from '@/shared/ui/image-upload-field'

interface PoiCategoryFormProps {
  icon: string
  iconActive: string
  color?: string
  translations: ISurveyTranslationRow[]
  languageCodes: string[]
  translationEntityId: string
  entityLabel: string
  isSubmitting?: boolean
  isSubmitDisabled?: boolean
  isIconSubmitDisabled?: boolean
  isColorSubmitDisabled?: boolean
  isLocalizedSubmitDisabled?: boolean
  isIconSubmitting?: boolean
  isColorSubmitting?: boolean
  isLocalizedSubmitting?: boolean
  submitLabel?: string
  iconSubmitLabel?: string
  colorSubmitLabel?: string
  localizedSubmitLabel?: string
  onIconChange: (value: string) => void
  onColorChange?: (value: string) => void
  onIconUploadingChange: (isUploading: boolean) => void
  onIconActiveChange: (value: string) => void
  onIconActiveUploadingChange: (isUploading: boolean) => void
  onTranslationsChange: (rows: ISurveyTranslationRow[]) => void
  onSubmit: () => void | Promise<void>
  onIconSubmit?: () => void | Promise<void>
  onColorSubmit?: () => void | Promise<void>
  onLocalizedSubmit?: () => void | Promise<void>
}

export function PoiCategoryForm({
  icon,
  iconActive,
  color,
  translations,
  languageCodes,
  translationEntityId,
  entityLabel,
  isSubmitting = false,
  isSubmitDisabled = false,
  isIconSubmitDisabled,
  isColorSubmitDisabled,
  isLocalizedSubmitDisabled,
  isIconSubmitting = false,
  isColorSubmitting = false,
  isLocalizedSubmitting = false,
  submitLabel,
  iconSubmitLabel,
  colorSubmitLabel,
  localizedSubmitLabel,
  onIconChange,
  onColorChange,
  onIconUploadingChange,
  onIconActiveChange,
  onIconActiveUploadingChange,
  onTranslationsChange,
  onSubmit,
  onIconSubmit,
  onColorSubmit,
  onLocalizedSubmit,
}: PoiCategoryFormProps) {
  const { t } = useTranslation()
  const hasSeparateSubmitActions = Boolean(onIconSubmit ?? onColorSubmit ?? onLocalizedSubmit)

  const categoryFields = useMemo(
    () => [
      {
        key: 'name' as const,
        label: t('poiCategories.name', { defaultValue: 'Name' }),
        placeholder: t('poiCategories.namePlaceholder', { defaultValue: 'Enter category name' }),
      },
    ],
    [t],
  )

  return (
    <Card className="mt-6 border-zinc-200">
      <CardHeader>
        <CardTitle className="text-base">
          {t('poiCategories.formTitle', { defaultValue: 'Category details' })}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form
          className="space-y-6"
          onSubmit={(event) => {
            event.preventDefault()
            void onSubmit()
          }}
        >
          <div className="space-y-3">
            <div className="flex gap-4">
              <div className="flex-1">
                <ImageUploadField
                  label={t('poiCategories.icon', { defaultValue: 'Icon' })}
                  value={icon}
                  onChange={onIconChange}
                  disabled={isSubmitting}
                  buttonSize="sm"
                  buttonClassName="w-full justify-start"
                  previewWrapperClassName="mt-3 flex h-28 w-28 items-center justify-center overflow-hidden rounded-lg border border-zinc-200 bg-zinc-50"
                  previewButtonClassName="h-full w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                  previewImageClassName="h-full w-full object-cover"
                  emptyTextClassName="px-3 text-center text-xs text-zinc-400"
                  texts={{
                    select: t('poi.media.uploadImage', { defaultValue: 'Upload image' }),
                    uploading: t('poi.media.uploadingImage', {
                      defaultValue: 'Uploading image...',
                    }),
                    empty: t('poiCategories.iconEmpty', { defaultValue: 'No icon' }),
                    previewAlt: t('poiCategories.icon', { defaultValue: 'Icon' }),
                  }}
                  onUploadingChange={onIconUploadingChange}
                  onUploadSuccess={() =>
                    showToast.success(
                      t('poi.media.imageUploadSuccess', { defaultValue: 'Image uploaded' }),
                    )
                  }
                  onUploadError={(message) =>
                    showToast.error(
                      message ||
                        t('poi.media.imageUploadFailed', { defaultValue: 'Image upload failed' }),
                    )
                  }
                  uploadFailedText={t('poi.media.imageUploadFailed', {
                    defaultValue: 'Image upload failed',
                  })}
                />
              </div>
              <div className="flex-1">
                <ImageUploadField
                  label={t('poiCategories.iconActive', { defaultValue: 'Active icon' })}
                  value={iconActive}
                  onChange={onIconActiveChange}
                  disabled={isSubmitting}
                  buttonSize="sm"
                  buttonClassName="w-full justify-start"
                  previewWrapperClassName="mt-3 flex h-28 w-28 items-center justify-center overflow-hidden rounded-lg border border-zinc-200 bg-zinc-50"
                  previewButtonClassName="h-full w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                  previewImageClassName="h-full w-full object-cover"
                  emptyTextClassName="px-3 text-center text-xs text-zinc-400"
                  texts={{
                    select: t('poi.media.uploadImage', { defaultValue: 'Upload image' }),
                    uploading: t('poi.media.uploadingImage', {
                      defaultValue: 'Uploading image...',
                    }),
                    empty: t('poiCategories.iconActiveEmpty', { defaultValue: 'No active icon' }),
                    previewAlt: t('poiCategories.iconActive', { defaultValue: 'Active icon' }),
                  }}
                  onUploadingChange={onIconActiveUploadingChange}
                  onUploadSuccess={() =>
                    showToast.success(
                      t('poi.media.imageUploadSuccess', { defaultValue: 'Image uploaded' }),
                    )
                  }
                  onUploadError={(message) =>
                    showToast.error(
                      message ||
                        t('poi.media.imageUploadFailed', { defaultValue: 'Image upload failed' }),
                    )
                  }
                  uploadFailedText={t('poi.media.imageUploadFailed', {
                    defaultValue: 'Image upload failed',
                  })}
                />
              </div>
            </div>
            {onIconSubmit && (
              <Button
                type="button"
                disabled={isIconSubmitDisabled ?? isSubmitDisabled}
                aria-busy={isIconSubmitting}
                onClick={() => {
                  void onIconSubmit()
                }}
              >
                {isIconSubmitting && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                {isIconSubmitting
                  ? t('poiCategories.updatingIcon', { defaultValue: 'Updating icon...' })
                  : (iconSubmitLabel ??
                    t('poiCategories.updateIcon', { defaultValue: 'Update icon' }))}
              </Button>
            )}
          </div>

          {color !== undefined && onColorChange && (
            <div className="space-y-3">
              <label
                htmlFor="poi-category-color"
                className="block text-sm font-medium text-zinc-700"
              >
                {t('poiCategories.color', { defaultValue: 'Color' })}
              </label>
              <div className="flex items-center gap-3">
                <input
                  id="poi-category-color"
                  type="color"
                  value={color}
                  disabled={isSubmitting}
                  onChange={(event) => onColorChange(event.target.value)}
                  className="h-11 w-20 cursor-pointer rounded border border-zinc-300 bg-white p-1 disabled:cursor-not-allowed disabled:opacity-50"
                />
                <span className="font-mono text-sm text-zinc-600">{color.toUpperCase()}</span>
              </div>
              {onColorSubmit && (
                <Button
                  type="button"
                  disabled={isColorSubmitDisabled ?? isSubmitDisabled}
                  aria-busy={isColorSubmitting}
                  onClick={() => {
                    void onColorSubmit()
                  }}
                >
                  {isColorSubmitting && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                  {isColorSubmitting
                    ? t('poiCategories.updatingColor', { defaultValue: 'Updating color...' })
                    : (colorSubmitLabel ??
                      t('poiCategories.updateColor', { defaultValue: 'Update color' }))}
                </Button>
              )}
            </div>
          )}

          {languageCodes.length > 0 ? (
            <SurveyTranslationSection
              blockTitle={t('survey.localizedBlockTitle', { defaultValue: 'Localized content' })}
              languages={languageCodes}
              translations={translations}
              onChange={onTranslationsChange}
              fields={categoryFields}
              entityId={translationEntityId}
              entityLabel={entityLabel}
              scope={TRANSLATION_JOB_SCOPE.POI_CATEGORY}
              {...(typeof window !== 'undefined' ? { entityPath: window.location.pathname } : {})}
            />
          ) : (
            <p className="text-sm text-amber-700">
              {t('survey.noTenantLanguages', {
                defaultValue: 'Configure tenant languages to edit localized fields.',
              })}
            </p>
          )}

          {onLocalizedSubmit && (
            <Button
              type="button"
              disabled={isLocalizedSubmitDisabled ?? isSubmitDisabled}
              aria-busy={isLocalizedSubmitting}
              onClick={() => {
                void onLocalizedSubmit()
              }}
            >
              {isLocalizedSubmitting && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
              {isLocalizedSubmitting
                ? t('poiCategories.updatingLocalized', { defaultValue: 'Updating localized...' })
                : (localizedSubmitLabel ??
                  t('poiCategories.updateLocalized', { defaultValue: 'Update localized' }))}
            </Button>
          )}

          {!hasSeparateSubmitActions && (
            <Button type="submit" disabled={isSubmitDisabled}>
              {submitLabel ?? t('common.create', { defaultValue: 'Create' })}
            </Button>
          )}
        </form>
      </CardContent>
    </Card>
  )
}
